import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, X, CheckCircle, AlertCircle } from "lucide-react";
import { useState, useRef, useCallback } from "react";
import { toast } from 'sonner';
import type { ExcelUpsertReport } from "@/types/task";

interface ExcelUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  onUpload: (file: File) => Promise<void>;
  isUploading?: boolean;
  progress?: number;
  uploadStatus?: 'idle' | 'uploading' | 'completed' | 'error';
  taskReport?: ExcelUpsertReport | null;
  onViewResults?: () => void;
}

export function ExcelUploadDialog({ 
  open, 
  onOpenChange, 
  title, 
  description, 
  onUpload,
  isUploading = false,
  progress = 0,
  uploadStatus = 'idle',
  taskReport = null,
  onViewResults
}: ExcelUploadDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((file: File) => {
    // 엑셀 파일 검증
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Excel 파일(.xlsx, .xls)만 업로드할 수 있습니다.");
      return;
    }

    // 파일 크기 제한 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("파일 크기는 10MB 이하만 업로드할 수 있습니다.");
      return;
    }

    setSelectedFile(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 1) {
      toast.error("파일은 1개만 업로드할 수 있습니다.");
      return;
    }

    if (files.length === 1) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 1) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("업로드할 파일을 선택해주세요.");
      return;
    }

    try {
      await onUpload(selectedFile);
    } catch (error) {
      console.error("업로드 오류:", error);
    }
  };

  const handleClose = () => {
    if (isUploading) return; // 업로드 중에는 닫기 불가
    
    setSelectedFile(null);
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onOpenChange(false);
  };

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case 'uploading':
        return <Upload className="h-12 w-12 text-blue-500 animate-pulse" />;
      case 'completed':
        return <CheckCircle className="h-12 w-12 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-12 w-12 text-red-500" />;
      default:
        return <Upload className="h-12 w-12 text-blue-500" />;
    }
  };

  const getStatusMessage = () => {
    switch (uploadStatus) {
      case 'uploading':
        return `${Math.round(progress)}% 업로드 진행중`;
      case 'completed':
        return "업로드가 완료되었습니다.";
      case 'error':
        return "업로드 중 오류가 발생했습니다.";
      default:
        return selectedFile ? `선택된 파일: ${selectedFile.name}` : "Excel 파일을 선택하거나 드래그해주세요";
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* 업로드 영역 */}
          <div
            className={`relative rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
              isDragging 
                ? 'border-primary bg-primary/5' 
                : 'border-muted-foreground/25 hover:border-muted-foreground/50'
            } ${
              isUploading 
                ? 'pointer-events-none opacity-60' 
                : uploadStatus === 'completed'
                ? 'pointer-events-none'
                : ''
            }`}
            onDrop={uploadStatus === 'completed' ? undefined : handleDrop}
            onDragOver={uploadStatus === 'completed' ? undefined : handleDragOver}
            onDragLeave={uploadStatus === 'completed' ? undefined : handleDragLeave}
            onClick={() => !(isUploading || uploadStatus === 'completed') && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileInputChange}
              className="hidden"
              disabled={isUploading || uploadStatus === 'completed'}
            />

            <div className="space-y-4">
              <div className="flex justify-center">
                {getStatusIcon()}
              </div>
              
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  {getStatusMessage()}
                </p>
                {!isUploading && !selectedFile && (
                  <p className="text-xs text-muted-foreground">
                    클릭하여 파일 선택 또는 드래그 앤 드롭
                  </p>
                )}
              </div>

              {/* 업로드 진행률 */}
              {(isUploading || uploadStatus === 'completed') && (
                <div className="space-y-2">
                  <Progress value={progress} className="w-full" />
                  <p className="text-xs text-muted-foreground">
                    진행률: {Math.round(progress)}%
                  </p>
                  {uploadStatus === 'completed' && onViewResults && taskReport && (
                    <p className="text-xs text-muted-foreground mt-2">
                      총 등록 {taskReport.totalCount}명 - 성공 {taskReport.successCount}명 / 실패 {taskReport.failCount}명
                    </p>
                  )}
                </div>
              )}

              {/* 선택된 파일 정보 */}
              {selectedFile && !isUploading && (
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <p className="text-sm font-medium">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    {(uploadStatus === 'idle' &&
                      <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                        >
                      <X className="h-4 w-4" />
                    </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              if (uploadStatus === 'completed' && onViewResults) {
                onViewResults();
              }
              handleClose();
            }}
            disabled={isUploading}
          >
            {uploadStatus === 'completed' ? '등록내역 확인' : '취소'}
          </Button>
          {uploadStatus !== 'completed' && (
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? '업로드 중...' : '업로드'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}